import { BACKEND_URL } from "config/getUrl";
import Cookies from "js-cookie";
import { logoutFunction, validateClient } from "./authRequests";
import blobToBase64 from "utils/blobToBase64";
import base64toBlob from "utils/base64toBlob";
import { getUserIdFromToken } from "./userRequests";
import { PresignedPost } from "utils/interfaces";
import { arrayBufferToBase64 } from "utils/bufferToBase64";

/**
 * Function used to get a scratch file from AWS
 * @param id _id of the lesson.
 * @param lessonType Type of lesson of interest. It could be "projects" or "modules".
 * @returns
 */
export const getScratchFileFromAWS = async (id: string, lessonType: string) => {
  try {
    // Validate the tokens
    const validateResponse = await validateClient();

    // Check if the user is still connected
    if (!validateResponse.connected) {
      return {
        message: "It was not possible to find an access or refresh token.",
        successful: false,
        logout: true,
      };
    }

    // Get the accessToken
    const accessToken = Cookies.get("accessToken");

    // To include it in the request
    const response = await fetch(
      `${BACKEND_URL}/api/code/scratch?type=${lessonType}&id=${id}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/x-scratch-project",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    // Check the health of the response
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the presigned url
    const presignedUrl = await response.json();

    // Try to get the file from this url
    const fileResponse = await fetch(presignedUrl);

    // Get the contentype
    const contentType = fileResponse.headers.get("Content-Type");

    // Check if the content type is correct
    if (contentType !== "application/x-scratch-project") {
      console.warn("Unexpected Content-Type:", contentType);
    }

    const arrayBuffer = await fileResponse.arrayBuffer();

    // Validate that it's a .sb3 file (check for ZIP file signature)
    const header = new Uint8Array(arrayBuffer.slice(0, 4));
    if (
      header[0] === 0x50 &&
      header[1] === 0x4b &&
      header[2] === 0x03 &&
      header[3] === 0x04
    ) {
      return arrayBuffer;
    } else {
      console.error(
        "Invalid file signature:",
        Array.from(header)
          .map((b) => b.toString(16).padStart(2, "0"))
          .join(" "),
      );
      throw new Error("Invalid file format. Expected .sb3 file (ZIP format).");
    }
  } catch (error) {
    console.error("Fetch error:", error);
  }
};

/**
 * Function used to fetch videos for code editors
 * @param video
 * @returns
 */
export const fetchVideoData = async (video: string) => {
  try {
    // Validate the tokens
    const validateResponse = await validateClient();

    // Check if the user is still connected
    if (!validateResponse.connected) {
      return "";
    }

    // Get the accessToken
    const accessToken = Cookies.get("accessToken");

    // Get the videoId
    const videoId = video.split("/").pop();

    // Request the video
    const response = await fetch(
      `${BACKEND_URL}/api/files/vimeothumbnail?id=${videoId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    // Processing
    if (response && response.ok && response.body) {
      // Get the body
      const jsonInfo = await response.json();

      // Check if the response has the thumbnail
      const thumbnail = jsonInfo.thumbnailUrl;

      return thumbnail ? thumbnail : "";
    }

    return "";
  } catch (error) {
    console.error("Error fetching video data", error);
    return "";
  }
};

/**
 * Function used to fetch a pdf
 * @param moduleOrProject
 * @param id
 * @returns
 */
export const fetchPdf = async (moduleOrProject: string, id: string) => {
  try {
    // Validate the tokens
    const validateResponse = await validateClient();

    // Check if the user is still connected
    if (!validateResponse.connected) {
      return "";
    }

    // Get the accessToken
    const accessToken = Cookies.get("accessToken");

    // Get the PDF
    const response = await fetch(
      `${BACKEND_URL}/api/files/${moduleOrProject}/pdf/${id}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    // Check if the response went right
    if (!response.ok) {
      console.error("Error PDF url");
      return "";
    }

    // Get the presigned url
    const presignedUrl = await response.json();

    // Try to get the file from this url
    const fileResponse = await fetch(presignedUrl);

    // Get the contentype
    const contentType = fileResponse.headers.get("Content-Type");

    // Check if the content type is correct
    if (contentType !== "application/pdf") {
      console.warn("Unexpected Content-Type for PDF fetching:", contentType);
    }

    // // Processing
    // const blob = await fileResponse.blob();

    // Processing the blob in order to force it to be a PDF content
    const arrayBuffer = await fileResponse.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: "application/pdf" });

    // Get the url of this blob
    const url = URL.createObjectURL(blob);

    return url;
  } catch (error) {
    console.error("Error fetching PDF:", error);
    return "";
  }
};

export const upsertCodeRequest = async (
  codeData: object | string | ArrayBuffer | null,
  codeEndpoint: "python-js" | "scratch",
) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Set up the options for the request
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(codeData),
  };

  // Post the record to the specified
  const response = await fetch(
    `${BACKEND_URL}/api/code/${codeEndpoint}`,
    options,
  );

  // Check if the status of the answer is ok
  if (response.status === 200) {
    // Get the response
    return await response.json();
  }
};

export const fetchImage = async (url: string) => {
  // Validate the tokens
  const validateResponse = await validateClient();

  // Check if the user is still connected
  if (!validateResponse.connected) {
    return {
      message: "It was not possible to find an access or refresh token.",
      successful: false,
      logout: true,
    };
  }

  // Get the accessToken
  const accessToken = Cookies.get("accessToken");

  // Check that the access token is not undefined
  if (!accessToken) {
    return {
      message: "It was not possible to find an access token.",
      successful: false,
      logout: true,
    };
  }

  // Sending to the server
  const options: RequestInit = {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  try {
    // Get the image
    const response = await fetch(`${BACKEND_URL}${url}`, options);

    // Check if the status of the answer is ok
    if (!response.ok)
      return {
        message: `Failed to fetch image: ${response.status} ${response.statusText}`,
        successful: false,
        logout: false,
      };

    // Get the image
    const blob = await response.blob();
    // And transform it into base64
    const imageBase64 = (await blobToBase64(blob)) as string;

    return {
      message: "Image fetched successfully!",
      content: imageBase64,
      successful: true,
      logout: false,
    };
  } catch (error) {
    console.error("Error fetching the image:", error);
    return {
      message: `Error fetching the image: ${error}`,
      successful: false,
      logout: false,
    };
  }
};

/**
 * Function used to send a base64 file into a s3 bucket
 * by using presigned post urls
 * @param file
 * @param key
 * @param presignedPost
 * @returns
 */
export const sendFilesToS3ByPresignedPost = async (
  file: string | null,
  key: string,
  presignedPost?: PresignedPost,
) => {
  // Check if the file is defined
  if (!(file && presignedPost)) return false;

  // Transform the base64 file into a blob
  const fileBlob = base64toBlob(file);

  // Get the content type
  const contentType = fileBlob.type;

  // Make sure that the type for JavaScript and Python
  // are texts
  const contentTypeOut = contentType.includes("javascript")
    ? "text/javascript"
    : contentType.includes("python")
      ? "text/x-python"
      : contentType;

  // Build a form for the request body
  const form = new FormData();

  // Appending all of the provided headers
  Object.keys(presignedPost.fields).forEach((key) => {
    if (key !== "userId") form.append(key, presignedPost.fields[key]);
  });

  // And the relevant information
  form.append("key", key);
  form.append("userId", getUserIdFromToken() || "");
  form.append("Content-Type", contentTypeOut);
  form.append("file", fileBlob);

  // Send the POST request
  const response = await fetch(presignedPost.url, {
    method: "POST",
    body: form,
  });

  if (!response.ok) return false;

  return true;
};

/**
 * Function used to get a instruction image from AWS
 * @param url Address or complete url to the resource.
 * @param includeBackendUrl (Optional) Boolean to decide if include the backend URL automatically.
 * @returns
 */
export const fetchPresignedUrlImage = async (
  url: string,
  includeBackendUrl: boolean = true,
) => {
  try {
    // Validate the tokens
    const validateResponse = await validateClient();

    // Check if the user is still connected
    if (!validateResponse.connected) {
      logoutFunction();
      return null;
    }

    // Get the accessToken
    const accessToken = Cookies.get("accessToken");

    const options: RequestInit = {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    // Define the target src
    let fetchInput;

    if (includeBackendUrl) {
      // Check if the url starts with a slash, otherwise add it
      const urlFormatted = url[0] === "/" ? url : `/${url}`;
      fetchInput = `${BACKEND_URL}${urlFormatted}`;
    } else fetchInput = url;

    // To include it in the request
    const response = await fetch(fetchInput, options);

    // Check the health of the response
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the presigned url
    const presignedUrl = await response.json();

    // Try to get the file from this url
    const fileResponse = await fetch(presignedUrl);

    // Get the contentype
    const contentType = fileResponse.headers.get("Content-Type");

    // Check if the response type is the expected
    // Check if the content type is correct
    if (!contentType?.startsWith("image/")) {
      console.warn("Unexpected Image-Type:", contentType);
      return null;
    }

    const blob = await fileResponse.blob();
    return blobToBase64(blob);
  } catch (error) {
    console.error("Fetch error:", error);
    return null;
  }
};

/**
 * Function used to get a instruction image from AWS
 * @param id _id of the lesson.
 * @param lessonType Type of lesson of interest. It could be "projects" or "modules".
 * @param hash Hash of the instruction image
 * @returns
 */
export const getInstructionImage = async (
  id: string,
  lessonType: string,
  hash: string,
) => {
  try {
    // Validate the tokens
    const validateResponse = await validateClient();

    // Check if the user is still connected
    if (!validateResponse.connected) {
      console.log("logout by this");
      logoutFunction();
      return null;
    }

    // Get the accessToken
    const accessToken = Cookies.get("accessToken");

    // To include it in the request
    const response = await fetch(
      `${BACKEND_URL}/api/imgs/instruction?lessonType=${lessonType}&id=${id}&hash=${hash}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/x-scratch-project",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    // Check the health of the response
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the presigned url
    const presignedUrl = await response.json();

    // Try to get the file from this url
    const fileResponse = await fetch(presignedUrl);
    const blob = await fileResponse.blob();
    return blobToBase64(blob);
  } catch (error) {
    console.error("Fetch error:", error);
  }
};
